import { useEffect } from 'react';

import { useSearchParams } from 'next/navigation';

import { segmentEventTypes } from '../externalTracking/types';
import { useExternalTracking } from '../externalTracking/useExternalTracking';
import { transformFiltersToActivityTrackerPayload } from '../filters/helpers';
import { sendBoostedAttributionClick } from '../impressions/api';
import { Components, TileType } from '../impressions/types';
import { useCurrentLocation } from '../location/useCurrentLocation';

import { createNewSearchId } from './getOrCreateNewSearchId';
import { sanitiseSearchQuery } from './helpers';
import { SearchSuggestionType } from './types';
import { useProductsSearchWithFallback } from './useProductsSearchWithFallback';
import { useSearchFilters } from './useSearchFilters';

import { SUGGESTION_TYPE_QUERY_KEY } from '@/components/SearchBar/SearchBar.hooks';
import {
  ActivityTrackerEventType,
  moduleOriginParamName,
} from '@/modules/activityTracker/constants';
import {
  createTrackingId,
  useActivityTracker,
} from '@/modules/activityTracker/useActivityTracker';
import { getProductsFromPaginatedResponse } from '@/modules/product/helpers/getProductsFromPaginatedResponse';
import { type PresApiSearchProduct } from '@/modules/search/aliases';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export type ClickHandlerArgs = {
  id: PresApiSearchProduct['id'];
  isBoosted?: PresApiSearchProduct['is_boosted'];
  pageNumber?: number;
  index: number;
};

export function useSearchTracking({ searchId }: { searchId: string }) {
  const searchParams = useSearchParams();
  const searchInput = searchParams.get('q') || '';
  /** @TECHDEBT PB 21/2/24
   * Ported this across as-is but there's no obvious reason that we
   * need to use to sanitised input here. It essentially adds a
   * duplicate useEffect too so would be nice to remove.
   */
  const sanitisedSearchInput = sanitiseSearchQuery(searchInput);
  const suggestionType = searchParams.get(SUGGESTION_TYPE_QUERY_KEY);
  const [, user] = useCurrentUser();
  const { location } = useCurrentLocation();
  const { data, isLoading } = useProductsSearchWithFallback(searchInput);
  const { searchFilters } = useSearchFilters();

  const products = getProductsFromPaginatedResponse(data) ?? [];
  const isEmptySearch = !isLoading && !products.length;
  const moduleOrigin =
    useSearchParams().get(moduleOriginParamName) || undefined;

  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });
  const {
    gtm: { sendEvent: sendGTMEvent },
    segment: { sendEvent: sendSegmentEvent },
  } = useExternalTracking();

  /** Runs on any search query sent to back end */
  useEffect(() => {
    createNewSearchId();

    if (suggestionType && suggestionType !== SearchSuggestionType.NONE) {
      sendActivityTrackerEvent(
        ActivityTrackerEventType.SEARCH_RESULTS_SUGGESTED_ACTION,
        {
          ...transformFiltersToActivityTrackerPayload(searchFilters),
          query: searchInput,
          depopSearchId: searchId,
          type: suggestionType,
          schemaVersion: '6.0',
        }
      );
    } else {
      const { categoryFilter, subcategoryFilter, ...payload } =
        transformFiltersToActivityTrackerPayload(searchFilters);

      sendActivityTrackerEvent(
        ActivityTrackerEventType.SEARCH_RESULTS_QUERY_ACTION,
        {
          ...payload,
          groups: searchFilters.groups ? [searchFilters.groups] : [],
          productTypes: searchFilters.productTypes ?? [],
          query: searchInput,
          depopSearchId: searchId,
        }
      );
    }
  }, [searchInput, JSON.stringify(searchFilters)]);

  /** Runs when new products are loaded */
  useEffect(() => {
    if (!searchInput || !products.length) {
      return;
    }

    sendSegmentEvent(segmentEventTypes.PRODUCTS_SEARCHED, {
      query: searchInput,
      product_ids: products.map((products) => String(products.id)),
      skus: products.map((product) => {
        if (!product.variants_all || product.variants_all.length === 0) {
          return String(product.id);
        }
        return `${product.id}-${product.variants_all[0].id}`;
      }),
      google_business_vertical: 'retail',
    });
  }, [products.length, searchInput]);

  /** Runs when the query string is changed, i.e. not filters */
  useEffect(() => {
    const eventId = createTrackingId();
    sendActivityTrackerEvent(
      sanitisedSearchInput === ''
        ? ActivityTrackerEventType.SEARCH_VIEW
        : ActivityTrackerEventType.SEARCH_RESULTS_VIEW,
      {
        query: sanitisedSearchInput,
        newSearch: true,
        depopSearchId: searchId,
        result: [],
      },
      {
        baseEventOverrides: { id: eventId },
      }
    );

    sendGTMEvent({
      event: 'search',
      search_term: searchInput,
      event_id: eventId,
    });

    if (moduleOrigin) {
      sendActivityTrackerEvent(
        ActivityTrackerEventType.WEB_MODULE_ATTRIBUTION_SEARCH_RESULTS_VIEW,
        {
          moduleName: moduleOrigin,
          schemaVersion: '1.0',
        }
      );
    }
  }, [searchInput]);

  useEffect(() => {
    if (isEmptySearch) {
      const { categoryFilter, subcategoryFilter, ...payload } =
        transformFiltersToActivityTrackerPayload(searchFilters);

      sendActivityTrackerEvent(
        ActivityTrackerEventType.EMPTY_SEARCH_RESULTS_VIEW,
        {
          query: searchInput,
          depopSearchId: searchId,
          locationFilter: location,
          ...payload,
        }
      );
    }
  }, [isEmptySearch]);

  function handleProductClick({
    index,
    id,
    isBoosted,
    pageNumber,
  }: ClickHandlerArgs) {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.SEARCH_RESULTS_PRODUCT_ACTION,
      {
        schemaVersion: '3.0',
        productId: id,
        position: index,
        depopSearchId: searchId,
        tileType: isBoosted ? TileType.Boosted : TileType.Organic,
        component: Components.InCountrySearch,
        page: pageNumber ?? 0,
        /**
         * we don't know the positionByTileType on web, but to use the v3
         * schema we need to pass this values
         */
        positionByTileType: 0,
      }
    );

    if (isBoosted) {
      sendBoostedAttributionClick(id);
    }
  }

  return { handleProductClick };
}
